import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HotelContext = createContext(null);

export function HotelProvider({ children }) {
  const [hotels, setHotels] = useState([]);
  const [activeHotel, setActiveHotel] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasMultipleHotels = hotels.length > 1;

  const fetchHotels = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/hotels/my-hotels`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setHotels(data.hotels || []);
        const active = (data.hotels || []).find(h => h.is_active);
        setActiveHotel(active || (data.hotels || [])[0] || null);
      }
    } catch { /* silent */ }
  }, []);

  // Initialize from login response stored in localStorage
  useEffect(() => {
    const hotelCtx = localStorage.getItem('hotel_context');
    if (hotelCtx) {
      try {
        const parsed = JSON.parse(hotelCtx);
        if (parsed.hotels) setHotels(parsed.hotels);
        if (parsed.active_hotel) setActiveHotel(parsed.active_hotel);
      } catch { /* ignore */ }
    }
    fetchHotels();
  }, [fetchHotels]);

  const switchHotel = useCallback(async (tenantId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/hotels/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId }),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Switched to ${data.hotel_name}`);
        setTimeout(() => window.location.reload(), 500);
        return true;
      }
    } catch {
      toast.error('Failed to switch hotel');
    } finally {
      setLoading(false);
    }
    return false;
  }, []);

  const addHotel = useCallback(async (hotelData) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/hotels/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hotelData),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchHotels();
        return data;
      }
    } catch {
      toast.error('Failed to add hotel');
    } finally {
      setLoading(false);
    }
    return null;
  }, [fetchHotels]);

  return (
    <HotelContext.Provider value={{
      hotels,
      activeHotel,
      hasMultipleHotels,
      loading,
      fetchHotels,
      switchHotel,
      addHotel,
    }}>
      {children}
    </HotelContext.Provider>
  );
}

export function useHotelContext() {
  const ctx = useContext(HotelContext);
  if (!ctx) return { hotels: [], activeHotel: null, hasMultipleHotels: false, loading: false, fetchHotels: () => {}, switchHotel: () => {}, addHotel: () => {} };
  return ctx;
}

export default HotelContext;
